/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { orgAppClient, OrgAppClientError } from '../services/orgAppClient';

export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
}

export interface Organization {
  id: string;
  legal_name: string;
  display_name: string;
  logo_url?: string;
  active_modules?: string[];
}

export interface OrgSession {
  user: User;
  current_organization: Organization | null;
  memberships: Array<{
    organization_id: string;
    organization_name: string;
  }>;
  active_memberships?: Array<{
    organization_id: string;
    organization_name: string;
  }>;
  role_keys: string[];
  permissions: string[];
  /** Platform admin connected as this org's admin ("Connect as Admin") —
   * gates admin-only tooling (e.g. AI filters from a competitor's history). */
  is_platform_admin_session: boolean;
}

type RawOrgSession = {
  user?: User;
  current_organization?: Organization | null;
  memberships?: Array<{ organization_id: string; organization_name: string }>;
  active_memberships?: Array<{ organization_id: string; organization_name: string }>;
  role_keys?: string[];
  permissions?: string[];
  is_platform_admin_session?: boolean;
};

function normalizeOrgSession(raw: RawOrgSession): OrgSession {
  const memberships = raw.memberships ?? raw.active_memberships ?? [];

  return {
    user: raw.user ?? { id: '', email: '', name: 'User' },
    current_organization: raw.current_organization ?? null,
    memberships,
    active_memberships: memberships,
    role_keys: Array.isArray(raw.role_keys) ? raw.role_keys : [],
    permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
    is_platform_admin_session: raw.is_platform_admin_session === true,
  };
}

interface OrgAuthContextType {
  session: OrgSession | null;
  isLoading: boolean;
  error: string | null;
  refreshSession: () => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
  logout: () => Promise<void>;
}

const OrgAuthContext = createContext<OrgAuthContextType | undefined>(undefined);

export function OrgAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<OrgSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const proactiveRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRef = useRef<OrgSession | null>(null);

  // Keep sessionRef in sync so the visibilitychange handler sees the latest value
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  function scheduleProactiveRefresh(expiresIn: number) {
    if (proactiveRefreshTimer.current) clearTimeout(proactiveRefreshTimer.current);
    // Refresh at 80% of remaining lifetime, minimum 5 s (clock-skew guard)
    const delayMs = Math.max(expiresIn * 0.8 * 1000, 5_000);
    proactiveRefreshTimer.current = setTimeout(() => {
      proactiveRefreshTimer.current = null;
      orgAppClient.refreshToken()
        .then((data) => {
          if (!data || data.authenticated === false) {
            setSession(null);
            return;
          }
          setSession(normalizeOrgSession(data as RawOrgSession));
          if (data.expires_in && data.expires_in > 0) {
            scheduleProactiveRefresh(data.expires_in);
          }
        })
        .catch((err: unknown) => {
          // Only clear the session when the server explicitly rejects the token.
          // Network errors (TypeError: Failed to fetch, etc.) should be silently
          // ignored so a brief connectivity loss does not log the user out.
          if (err instanceof OrgAppClientError) setSession(null);
        });
    }, delayMs);
  }

  const refreshSession = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await orgAppClient.getSession();
      if (data && data.authenticated === false) {
        setSession(null);
      } else {
        setSession(normalizeOrgSession(data as RawOrgSession));
        if (data?.expires_in && data.expires_in > 0) {
          scheduleProactiveRefresh(data.expires_in);
        }
      }
    } catch (err: unknown) {
      setSession(null);
      const error = err as { code?: string; message?: string };
      if (!['AUTH_REQUIRED', 'AUTH_INVALID_TOKEN'].includes(error.code ?? '')) {
        setError(error.message || 'Failed to load session');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const switchOrganization = async (orgId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await orgAppClient.switchOrganization(orgId);
      setSession(normalizeOrgSession(data as RawOrgSession));
      if (data?.expires_in && data.expires_in > 0) {
        scheduleProactiveRefresh(data.expires_in);
      }
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to switch organization');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Cancel any pending proactive refresh so it cannot race with the logout
    // request and re-set the session after we clear it.
    if (proactiveRefreshTimer.current) {
      clearTimeout(proactiveRefreshTimer.current);
      proactiveRefreshTimer.current = null;
    }
    setIsLoading(true);
    try {
      await orgAppClient.logout();
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to logout');
    } finally {
      // Always clear the local session regardless of whether the server request
      // succeeded, so the user is never left in a stuck "logged-in" state.
      setSession(null);
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refreshSession();
    return () => {
      if (proactiveRefreshTimer.current) clearTimeout(proactiveRefreshTimer.current);
    };
  }, []);

  // Silently refresh the token whenever the user returns to this tab
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && sessionRef.current) {
        orgAppClient.refreshToken()
          .then((data) => {
            if (!data || data.authenticated === false) {
              setSession(null);
              return;
            }
            setSession(normalizeOrgSession(data as RawOrgSession));
            if (data?.expires_in && data.expires_in > 0) {
              scheduleProactiveRefresh(data.expires_in);
            }
          })
          .catch((err: unknown) => {
            // Only clear the session when the server explicitly rejects the token.
            // Network errors (TypeError: Failed to fetch, etc.) should be silently
            // ignored so a brief connectivity loss does not log the user out.
            if (err instanceof OrgAppClientError) setSession(null);
          });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <OrgAuthContext.Provider
      value={{
        session,
        isLoading,
        error,
        refreshSession,
        switchOrganization,
        logout,
      }}
    >
      {children}
    </OrgAuthContext.Provider>
  );
}

export function useOrgAuth() {
  const context = useContext(OrgAuthContext);
  if (context === undefined) {
    throw new Error('useOrgAuth must be used within an OrgAuthProvider');
  }
  return context;
}
